// src/pages/SettingsPage.tsx
import { Settings as SettingsIcon, Users, Bell, Sliders, Edit2, Trash2, UserPlus } from "lucide-react";
import { useState, type CSSProperties } from "react";

type TabType = "general" | "user-access" | "alert-preferences" | "preferences";

type TeamMember = {
  id: number;
  name: string;
  role: "Super Admin" | "Admin" | "User";
  email: string;
  lastActive: string;
};

function ToggleSwitch({
  checked,
  onChange,
  id,
  ariaLabel,
  variant = "alert",
}: Readonly<{
  checked: boolean;
  onChange: () => void;
  id?: string;
  ariaLabel?: string;
  variant?: "alert" | "preference";
}>) {
  const activeColor = variant === "alert" ? "bg-[#EF4444]" : "bg-[#D32F2F]";
  const ringStyle =
    variant === "alert"
      ? "focus-visible:ring-[#EF4444]/35 focus-visible:ring-offset-0"
      : "focus-visible:ring-[#D32F2F]/35 focus-visible:ring-offset-2";

  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onChange}
      className={`relative h-6 w-11 shrink-0 cursor-pointer overflow-hidden rounded-full border-0 shadow-none outline-none transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 ${ringStyle} ${
        checked ? activeColor : "bg-[#EDEDED]"
      }`}
    >
      <span
        className={`pointer-events-none absolute top-1 h-4 w-4 rounded-full bg-white shadow-none transition-[left,right] duration-200 ease-in-out ${
          checked ? "left-auto right-1" : "left-1 right-auto"
        }`}
      />
    </button>
  );
}

export default function SettingsPage() {
  const [selectedTab, setSelectedTab] = useState<TabType>("general");
  const [companyName, setCompanyName] = useState("WorldLink Global Logistics");
  const [industry, setIndustry] = useState("Logistics & Supply Chain");
  const [timeZone, setTimeZone] = useState("UTC-05:00 (EST)");
  const [defaultCurrency, setDefaultCurrency] = useState("USD");
  const [defaultTradeLanes, setDefaultTradeLanes] = useState("Asia-Pacific, North America");
  const [defaultRiskThreshold, setDefaultRiskThreshold] = useState("Medium (30-50%)");
  const [defaultOptimizationStrategy, setDefaultOptimizationStrategy] = useState("Balanced (Cost + Risk)");

  // Alert Preferences toggles: off = light track + knob left; on = red + knob right
  const [weatherAlerts, setWeatherAlerts] = useState(true);
  const [slaBreachAlerts, setSlaBreachAlerts] = useState(true);
  const [tariffChanges, setTariffChanges] = useState(true);
  const [highRiskRoutes, setHighRiskRoutes] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);

  // Application Preferences toggles (defaults align with design ref: two off, reduced motion on)
  const [darkMode, setDarkMode] = useState(false);
  const [compactTableView, setCompactTableView] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [defaultLandingPage, setDefaultLandingPage] = useState("Dashboard");

  const teamMembers: TeamMember[] = [
    { id: 1, name: "Sarah Johnson", role: "Super Admin", email: "sarah.johnson@worldlink.com", lastActive: "2 hours ago" },
    { id: 2, name: "Michael Chen", role: "Admin", email: "michael.chen@worldlink.com", lastActive: "1 day ago" },
    { id: 3, name: "Emily Rodriguez", role: "User", email: "emily.rodriguez@worldlink.com", lastActive: "3 hours ago" },
    { id: 4, name: "David Park", role: "User", email: "david.park@worldlink.com", lastActive: "5 days ago" },
  ];

  /** User Access — role pill colors per design spec (hex). */
  const roleColors = {
    "Super Admin": "text-[#7C3AED] bg-[#F5F3FF]",
    "Admin": "text-[#991B1B] bg-[#FEF2F2]",
    "User": "text-[#1E40AF] bg-[#EFF6FF]",
  };

  /** General tab: same box model; focus 2px vivid red, no ring. */
  const generalFieldStyle: CSSProperties = {
    boxShadow: "none",
  };

  return (
    <div className="min-h-full bg-[#F5F5F5] p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-black">System Settings</h1>
        <p className="text-sm text-gray-600 mt-1">Manage system preferences, user access, and configuration.</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-6 mb-6">
        <button
          onClick={() => setSelectedTab("general")}
          className={`pb-3 px-1 text-sm font-medium transition-colors flex items-center gap-2 ${
            selectedTab === "general"
              ? "border-b-2 border-red-600 text-red-600 bg-transparent"
              : "text-gray-600 hover:text-gray-900 bg-transparent"
          }`}
          style={{ 
            border: '0',
            borderBottom: selectedTab === "general" ? '2px solid #dc2626' : '0',
            outline: '0',
            boxShadow: 'none'
          }}
        >
          <SettingsIcon className="size-4" />
          General
        </button>
        <button
          onClick={() => setSelectedTab("user-access")}
          className={`pb-3 px-1 text-sm font-medium transition-colors flex items-center gap-2 ${
            selectedTab === "user-access"
              ? "border-b-2 border-red-600 text-red-600 bg-transparent"
              : "text-gray-600 hover:text-gray-900 bg-transparent"
          }`}
          style={{ 
            border: '0',
            borderBottom: selectedTab === "user-access" ? '2px solid #dc2626' : '0',
            outline: '0',
            boxShadow: 'none'
          }}
        >
          <Users className="size-4" />
          User Access
        </button>
        <button
          onClick={() => setSelectedTab("alert-preferences")}
          className={`pb-3 px-1 text-sm font-medium transition-colors flex items-center gap-2 ${
            selectedTab === "alert-preferences"
              ? "border-b-2 border-red-600 text-red-600 bg-transparent"
              : "text-gray-600 hover:text-gray-900 bg-transparent"
          }`}
          style={{ 
            border: '0',
            borderBottom: selectedTab === "alert-preferences" ? '2px solid #dc2626' : '0',
            outline: '0',
            boxShadow: 'none'
          }}
        >
          <Bell className="size-4" />
          Alert Preferences
        </button>
        <button
          onClick={() => setSelectedTab("preferences")}
          className={`pb-3 px-1 text-sm font-medium transition-colors flex items-center gap-2 ${
            selectedTab === "preferences"
              ? "border-b-2 border-red-600 text-red-600 bg-transparent"
              : "text-gray-600 hover:text-gray-900 bg-transparent"
          }`}
          style={{ 
            border: '0',
            borderBottom: selectedTab === "preferences" ? '2px solid #dc2626' : '0',
            outline: '0',
            boxShadow: 'none'
          }}
        >
          <Sliders className="size-4" />
          Preferences
        </button>
      </div>

      {/* General Tab */}
      {selectedTab === "general" && (
        <div className="space-y-6">
          {/* Company Profile */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-6">Company Profile</h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label htmlFor="settings-company-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    id="settings-company-name"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full h-[38px] px-4 py-2 rounded-xl text-sm border-2 border-solid border-wl-border bg-white text-gray-900 shadow-none focus:outline-none focus:ring-0 focus:border-[#E3182A]"
                    style={generalFieldStyle}
                  />
                </div>
                <div>
                  <label htmlFor="settings-industry" className="block text-sm font-medium text-gray-700 mb-2">
                    Industry
                  </label>
                  <input
                    id="settings-industry"
                    type="text"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full h-[38px] px-4 py-2 rounded-xl text-sm border-2 border-solid border-wl-border bg-white text-gray-900 shadow-none focus:outline-none focus:ring-0 focus:border-[#E3182A]"
                    style={generalFieldStyle}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label htmlFor="settings-time-zone" className="block text-sm font-medium text-gray-700 mb-2">
                    Time Zone
                  </label>
                  <select
                    id="settings-time-zone"
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    className="w-full h-[38px] px-4 py-2 rounded-xl text-sm border-2 border-solid border-wl-border bg-white text-gray-900 shadow-none focus:outline-none focus:ring-0 focus:border-[#E3182A]"
                    style={generalFieldStyle}
                  >
                    <option value="UTC-05:00 (EST)">UTC-05:00 (EST)</option>
                    <option value="UTC-06:00 (CST)">UTC-06:00 (CST)</option>
                    <option value="UTC-07:00 (MST)">UTC-07:00 (MST)</option>
                    <option value="UTC-08:00 (PST)">UTC-08:00 (PST)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="settings-default-currency" className="block text-sm font-medium text-gray-700 mb-2">
                    Default Currency
                  </label>
                  <select
                    id="settings-default-currency"
                    value={defaultCurrency}
                    onChange={(e) => setDefaultCurrency(e.target.value)}
                    className="w-full h-[38px] px-4 py-2 rounded-xl text-sm border-2 border-solid border-wl-border bg-white text-gray-900 shadow-none focus:outline-none focus:ring-0 focus:border-[#E3182A]"
                    style={generalFieldStyle}
                  >
                    <option value="USD - US Dollar">USD - US Dollar</option>
                    <option value="EUR - Euro">EUR - Euro</option>
                    <option value="GBP - British Pound">GBP - British Pound</option>
                    <option value="JPY - Japanese Yen">JPY - Japanese Yen</option>
                  </select>
                </div>
              </div>
              
              <div className="pt-2">
                <button className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 text-sm font-medium border-none">
                  Save Changes
                </button>
              </div>
            </div>
          </div>

          {/* Regional Defaults */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-6">Regional Defaults</h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label htmlFor="settings-trade-lanes" className="block text-sm font-medium text-gray-700 mb-2">
                    Default Trade Lanes
                  </label>
                  <input
                    id="settings-trade-lanes"
                    type="text"
                    value={defaultTradeLanes}
                    onChange={(e) => setDefaultTradeLanes(e.target.value)}
                    className="w-full h-[38px] px-4 py-2 rounded-xl text-sm border-2 border-solid border-wl-border bg-white text-gray-900 shadow-none focus:outline-none focus:ring-0 focus:border-[#E3182A]"
                    style={generalFieldStyle}
                  />
                </div>
                <div>
                  <label htmlFor="settings-risk-threshold" className="block text-sm font-medium text-gray-700 mb-2">
                    Risk Threshold
                  </label>
                  <select
                    id="settings-risk-threshold"
                    value={defaultRiskThreshold}
                    onChange={(e) => setDefaultRiskThreshold(e.target.value)}
                    className="w-full h-[38px] px-4 py-2 rounded-xl text-sm border-2 border-solid border-wl-border bg-white text-gray-900 shadow-none focus:outline-none focus:ring-0 focus:border-[#E3182A]"
                    style={generalFieldStyle}
                  >
                    <option value="Low (10-20%)">Low (10-20%)</option>
                    <option value="Medium (30-50%)">Medium (30-50%)</option>
                    <option value="High (60-80%)">High (60-80%)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label htmlFor="settings-optimization-strategy" className="block text-sm font-medium text-gray-700 mb-2">
                    Optimization Strategy
                  </label>
                  <select
                    id="settings-optimization-strategy"
                    value={defaultOptimizationStrategy}
                    onChange={(e) => setDefaultOptimizationStrategy(e.target.value)}
                    className="w-full h-[38px] px-4 py-2 rounded-xl text-sm border-2 border-solid border-wl-border bg-white text-gray-900 shadow-none focus:outline-none focus:ring-0 focus:border-[#E3182A]"
                    style={generalFieldStyle}
                  >
                    <option value="Cost Optimized">Cost Optimized</option>
                    <option value="Balanced (Cost + Risk)">Balanced (Cost + Risk)</option>
                    <option value="Risk Minimized">Risk Minimized</option>
                  </select>
                </div>
              </div>
              
              <div className="pt-2">
                <button className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 text-sm font-medium border-none">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Access Tab */}
      {selectedTab === "user-access" && (
        <div className="rounded-xl border border-[#E5E5E5] bg-white p-6 font-sans shadow-none sm:p-8">
          <div className="mb-8 flex flex-col gap-4 sm:mb-10 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-bold not-italic tracking-tight text-[#111827]">Team Members</h2>
            <button
              type="button"
              className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg border-0 bg-[#E11D48] px-4 py-2.5 text-sm font-medium not-italic text-white shadow-none transition-colors hover:bg-[#C91840] sm:w-auto"
            >
              <UserPlus className="size-4 shrink-0" aria-hidden />
              Invite User
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left font-sans not-italic antialiased">
              <thead>
                <tr className="border-b border-gray-200">
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-normal uppercase tracking-wide text-[#6B7280]"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-normal uppercase tracking-wide text-[#6B7280]"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-normal uppercase tracking-wide text-[#6B7280]"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-normal uppercase tracking-wide text-[#6B7280]"
                  >
                    Last Active
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-normal uppercase tracking-wide text-[#6B7280]"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.map((member) => (
                  <tr key={member.id} className="border-b border-gray-200 last:border-b-0">
                    <td className="px-6 py-[18px] align-middle">
                      <span className="text-sm font-bold not-italic text-[#111827]">{member.name}</span>
                    </td>
                    <td className="px-6 py-[18px] align-middle">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium not-italic ${roleColors[member.role]}`}
                      >
                        {member.role}
                      </span>
                    </td>
                    <td className="px-6 py-[18px] align-middle">
                      <span className="text-sm font-normal not-italic lowercase text-[#374151]">
                        {member.email}
                      </span>
                    </td>
                    <td className="px-6 py-[18px] align-middle">
                      <span className="text-sm font-normal not-italic text-[#6B7280]">{member.lastActive}</span>
                    </td>
                    <td className="px-6 py-[18px] align-middle">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="rounded-md border-0 bg-transparent p-2 text-[#9CA3AF] shadow-none outline-none transition-colors hover:bg-gray-50 hover:text-[#6B7280] focus-visible:ring-2 focus-visible:ring-[#E11D48]/30 focus-visible:ring-offset-0"
                          title="Edit user"
                        >
                          <Edit2 className="size-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="rounded-md border-0 bg-transparent p-2 text-[#EF4444] shadow-none outline-none transition-colors hover:bg-[#FEF2F2] focus-visible:ring-2 focus-visible:ring-[#EF4444]/35 focus-visible:ring-offset-0"
                          title="Delete user"
                        >
                          <Trash2 className="size-4" aria-hidden />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alert Preferences Tab — spacing/typography per design ref: section gaps ~48px, rows ~24px, title→body tight */}
      {selectedTab === "alert-preferences" && (
        <div className="rounded-xl border border-[#E5E5E5] bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.05)] sm:p-8">
          <h2 className="mb-10 text-base font-bold not-italic tracking-tight text-[#111827]">
            Notification Preferences
          </h2>

          <div className="flex flex-col gap-12">
            {/* Alert Types */}
            <div>
              <h3 className="mb-6 text-sm font-bold not-italic text-black">Alert Types</h3>
              <div className="flex flex-col space-y-6">
                <div className="flex items-center justify-between gap-6">
                  <label
                    htmlFor="toggle-weather-alerts"
                    className="min-w-0 flex-1 cursor-pointer pr-4 select-none"
                  >
                    <div className="mb-0.5 text-sm font-normal not-italic text-black">Weather Alerts</div>
                    <div className="text-xs font-normal leading-relaxed text-[#6B7280]">
                      Receive notifications about severe weather conditions affecting routes
                    </div>
                  </label>
                  <ToggleSwitch id="toggle-weather-alerts" checked={weatherAlerts} onChange={() => setWeatherAlerts((v) => !v)} />
                </div>

                <div className="flex items-center justify-between gap-6">
                  <label
                    htmlFor="toggle-sla-breach-alerts"
                    className="min-w-0 flex-1 cursor-pointer pr-4 select-none"
                  >
                    <div className="mb-0.5 text-sm font-normal not-italic text-black">SLA Breach Alerts</div>
                    <div className="text-xs font-normal leading-relaxed text-[#6B7280]">
                      Get notified when shipments are at risk of missing deadlines
                    </div>
                  </label>
                  <ToggleSwitch id="toggle-sla-breach-alerts" checked={slaBreachAlerts} onChange={() => setSlaBreachAlerts((v) => !v)} />
                </div>

                <div className="flex items-center justify-between gap-6">
                  <label
                    htmlFor="toggle-tariff-changes"
                    className="min-w-0 flex-1 cursor-pointer pr-4 select-none"
                  >
                    <div className="mb-0.5 text-sm font-normal not-italic text-black">Tariff Changes</div>
                    <div className="text-xs font-normal leading-relaxed text-[#6B7280]">
                      Stay updated on changes to trade tariffs and regulations
                    </div>
                  </label>
                  <ToggleSwitch id="toggle-tariff-changes" checked={tariffChanges} onChange={() => setTariffChanges((v) => !v)} />
                </div>

                <div className="flex items-center justify-between gap-6">
                  <label
                    htmlFor="toggle-high-risk-routes"
                    className="min-w-0 flex-1 cursor-pointer pr-4 select-none"
                  >
                    <div className="mb-0.5 text-sm font-normal not-italic text-black">High Risk Routes</div>
                    <div className="text-xs font-normal leading-relaxed text-[#6B7280]">
                      Alerts for routes with elevated risk factors
                    </div>
                  </label>
                  <ToggleSwitch id="toggle-high-risk-routes" checked={highRiskRoutes} onChange={() => setHighRiskRoutes((v) => !v)} />
                </div>
              </div>
            </div>

            {/* Delivery Methods */}
            <div>
              <h3 className="mb-6 text-sm font-bold not-italic text-black">Delivery Methods</h3>
              <div className="flex flex-col space-y-6">
                <div className="flex items-center justify-between gap-6">
                  <label
                    htmlFor="toggle-email-notifications"
                    className="min-w-0 flex-1 cursor-pointer pr-4 select-none"
                  >
                    <div className="mb-0.5 text-sm font-normal not-italic text-black">Email Notifications</div>
                    <div className="text-xs font-normal leading-relaxed text-[#6B7280]">
                      Receive notifications via email
                    </div>
                  </label>
                  <ToggleSwitch id="toggle-email-notifications" checked={emailNotifications} onChange={() => setEmailNotifications((v) => !v)} />
                </div>

                <div className="flex items-center justify-between gap-6">
                  <label
                    htmlFor="toggle-in-app-notifications"
                    className="min-w-0 flex-1 cursor-pointer pr-4 select-none"
                  >
                    <div className="mb-0.5 text-sm font-normal not-italic text-black">In-App Notifications</div>
                    <div className="text-xs font-normal leading-relaxed text-[#6B7280]">
                      Show notifications within the platform
                    </div>
                  </label>
                  <ToggleSwitch id="toggle-in-app-notifications" checked={inAppNotifications} onChange={() => setInAppNotifications((v) => !v)} />
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="mt-12 rounded-lg border-0 bg-[#D32F2F] px-6 py-2.5 text-sm font-medium text-white shadow-none transition-colors hover:bg-[#B71C1C]"
          >
            Save Preferences
          </button>
        </div>
      )}

      {/* Preferences Tab — card border/shadow/padding match Alert Preferences tab */}
      {selectedTab === "preferences" && (
        <div className="rounded-xl border border-[#E5E5E5] bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.05)] sm:p-8">
          <h2 className="mb-10 text-base font-bold not-italic tracking-tight text-[#111827]">
            Application Preferences
          </h2>

          <div className="flex flex-col gap-8">
            <div className="flex items-center justify-between gap-8">
              <div className="min-w-0 flex-1 pr-6">
                <div className="mb-1 text-sm font-bold text-black">Dark Mode</div>
                <div className="text-xs font-normal leading-relaxed text-[#666666]">
                  Use dark theme throughout the application
                </div>
              </div>
              <ToggleSwitch checked={darkMode} onChange={() => setDarkMode((v) => !v)} ariaLabel="Toggle dark mode" variant="preference" />
            </div>

            <div className="flex items-center justify-between gap-8">
              <div className="min-w-0 flex-1 pr-6">
                <div className="mb-1 text-sm font-bold text-black">Compact Table View</div>
                <div className="text-xs font-normal leading-relaxed text-[#666666]">
                  Show more data in tables with reduced row height
                </div>
              </div>
              <ToggleSwitch checked={compactTableView} onChange={() => setCompactTableView((v) => !v)} variant="preference" />
            </div>

            <div className="flex items-center justify-between gap-8">
              <div className="min-w-0 flex-1 pr-6">
                <div className="mb-1 text-sm font-bold text-black">Reduced Motion</div>
                <div className="text-xs font-normal leading-relaxed text-[#666666]">
                  Minimize animations and transitions
                </div>
              </div>
              <ToggleSwitch checked={reducedMotion} onChange={() => setReducedMotion((v) => !v)} variant="preference" />
            </div>

            <div className="pt-2">
              <label
                htmlFor="settings-default-landing-page"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Default Landing Page
              </label>
              <select
                id="settings-default-landing-page"
                value={defaultLandingPage}
                onChange={(e) => setDefaultLandingPage(e.target.value)}
                className="h-[38px] w-full rounded-xl border-2 border-solid border-wl-border bg-white px-4 py-2 text-sm font-normal text-gray-900 shadow-none focus:border-[#E3182A] focus:outline-none focus:ring-0 sm:max-w-[38%]"
                style={generalFieldStyle}
              >
                <option value="Dashboard">Dashboard</option>
                <option value="Orders">Orders</option>
                <option value="Tariff Calculator">Tariff Calculator</option>
                <option value="Route Optimization">Route Optimization</option>
              </select>
            </div>
          </div>

          <button
            type="button"
            className="mt-14 rounded-lg border-0 bg-[#D32F2F] px-6 py-2.5 text-sm font-bold text-white shadow-none transition-colors hover:bg-[#B71C1C]"
          >
            Save Preferences
          </button>
        </div>
      )}
    </div>
  );
}
